import { getDefaultLang } from "@cervval.js/common/src/i18n/languages";
import { ONAPS_AGE, ONAPS_CANCER, ONAPS_DIABETES, ONAPS_FUNCTIONAL, ONAPS_HEART, ONAPS_INFECTIOUS, ONAPS_INTESTINAL, ONAPS_KIDNEY, ONAPS_MUSCULOSKELETAL, ONAPS_NEUROLOGIC, ONAPS_OVERWEIGHT, ONAPS_PREGNANCY, ONAPS_PSYCHIATRY, ONAPS_RESPIRATORY, ONAPS_STATE_OR_PATHOLOGIES, ONAPS_TRANSPLANTED, PCS, SPORT_ACTIVITY_MODE } from "./onaps.types";

export type _I18nOnaps = {
    pcs: { [key in PCS]: string },
    stateOrPathologies: {
        general: { [key in ONAPS_STATE_OR_PATHOLOGIES]: string },
        overweight: { [key in ONAPS_OVERWEIGHT]: string },
        heart: { [key in ONAPS_HEART]: string },
        diabetes: { [key in ONAPS_DIABETES]: string },
        cancer: { [key in ONAPS_CANCER]: string },
        neurologic: { [key in ONAPS_NEUROLOGIC]: string },
        psychiatry: { [key in ONAPS_PSYCHIATRY]: string },
        musculoskeletal: { [key in ONAPS_MUSCULOSKELETAL]: string },
        respiratory: { [key in ONAPS_RESPIRATORY]: string },
        infectious: { [key in ONAPS_INFECTIOUS]: string },
        kidney: { [key in ONAPS_KIDNEY]: string },
        intestinal: { [key in ONAPS_INTESTINAL]: string },
        functional: { [key in ONAPS_FUNCTIONAL]: string },
        transplanted: { [key in ONAPS_TRANSPLANTED]: string },
        pregnancy: { [key in ONAPS_PREGNANCY]: string },
        age: { [key in ONAPS_AGE]: string }
    },
    sportActivityMode: { [key in SPORT_ACTIVITY_MODE]: string }
}

const _fr: _I18nOnaps = {
    pcs: {
        [PCS.FARMER]: "Agriculteurs exploitants",
        [PCS.EMPLOYEE]: "Employés (employé de bureau ou de commerce, garde d'enfants, agent de service...)",
        [PCS.INTERMEDIARY]: "Professions intermédiaires (professeur des écoles, infirmier, assistant social, technicien, contremaître...)",
        [PCS.LEADER]: "Artisans, commerçants et chefs d'entreprise",
        [PCS.MANAGER]: "Cadres et professions intellectuelles supérieures (ingénieur, médecin...)",
        [PCS.OTHER_INACTIVE]: "Autres inactifs (par exemple parent au foyer)",
        [PCS.RETIRED]: "Retraités",
        [PCS.STUDENT]: "Etudiants",
        [PCS.WORKER]: "Ouvriers (y compris agricoles)"
    },
    stateOrPathologies: {
        general: {
            [ONAPS_STATE_OR_PATHOLOGIES.OVERWEIGHT]: "Surpoids / Obésité",
            [ONAPS_STATE_OR_PATHOLOGIES.HEART]: "Cardiovasculaires",
            [ONAPS_STATE_OR_PATHOLOGIES.DIABETES]: "Diabètes",
            [ONAPS_STATE_OR_PATHOLOGIES.CANCER]: "Cancers",
            [ONAPS_STATE_OR_PATHOLOGIES.NEUROLOGIC]: "Neurologies",
            [ONAPS_STATE_OR_PATHOLOGIES.PSYCHIATRY]: "Psychiatrie",
            [ONAPS_STATE_OR_PATHOLOGIES.MUSCULOSKELETAL]: "Ostéoarticulaires",
            [ONAPS_STATE_OR_PATHOLOGIES.RESPIRATORY]: "Respiratoires",
            [ONAPS_STATE_OR_PATHOLOGIES.INFECTIOUS]: "Infectieuses",
            [ONAPS_STATE_OR_PATHOLOGIES.KIDNEY]: "Rénales",
            [ONAPS_STATE_OR_PATHOLOGIES.INTESTINAL]: "Intestinales chroniques inflammatoires",
            [ONAPS_STATE_OR_PATHOLOGIES.FUNCTIONAL]: "Symptômes fonctionnels",
            [ONAPS_STATE_OR_PATHOLOGIES.TRANSPLANTED]: "Transplantés",
            [ONAPS_STATE_OR_PATHOLOGIES.OTHER]: "Autres",
            [ONAPS_STATE_OR_PATHOLOGIES.AGE]: "Vieillissement",
            [ONAPS_STATE_OR_PATHOLOGIES.PREGNANCY]: "Grossesse non pathologique"
        },
        overweight: {
            [ONAPS_OVERWEIGHT.OBESITY]: "Obésité",
            [ONAPS_OVERWEIGHT.OVERWEIGHT]: "Surpoids",
            [ONAPS_OVERWEIGHT.PRE_BARIATRIC]: "Préparation chirurgie bariatrique",
            [ONAPS_OVERWEIGHT.POST_BARIATRIC]: "Post chirurgie bariatrique",
            [ONAPS_OVERWEIGHT.METABOLIC]: "Syndrome métabolique",
            [ONAPS_OVERWEIGHT.STEATOSIS]: "Stéatose hépatique non alcoolique",
            [ONAPS_OVERWEIGHT.OTHER]: "Autres"
        },
        heart: {
            [ONAPS_HEART.HYPERTENSION]: "Hypertension artérielle",
            [ONAPS_HEART.CORONARY]: "Infarctus du myocarde / maladie coronaire",
            [ONAPS_HEART.HEART_FAILURE]: "Insuffisance cardiaque",
            [ONAPS_HEART.RHYTHM_TROUBLE]: "Trouble du rythme cardiaque",
            [ONAPS_HEART.CONGENITAL]: "Cardiopathie congénitale",
            [ONAPS_HEART.VALVULAR]: "Maladies valvulaires",
            [ONAPS_HEART.PULMONARY_EMBOLISM]: "Embolie pulmonaire",
            [ONAPS_HEART.OTHER]: "Autres"
        },
        diabetes: {
            [ONAPS_DIABETES.TYPE_2_UNTREATED]: "Type 2 non traité",
            [ONAPS_DIABETES.TYPE_2_TREATED]: "Type 2 traité sans complications",
            [ONAPS_DIABETES.TYPE_2_COMPLICATIONS]: "Type 2 traité avec complication",
            [ONAPS_DIABETES.TYPE_1]: "Type 1 non compliqué",
            [ONAPS_DIABETES.TYPE_1_COMPLICATIONS]: "Type 1 compliqué",
            [ONAPS_DIABETES.GESTATIONAL]: "Gestationnel"
        },
        cancer: {
            [ONAPS_CANCER.BREAST]: "Sein",
            [ONAPS_CANCER.DIGESTIVE]: "Colon / digestifs",
            [ONAPS_CANCER.UTERUS]: "Utérus",
            [ONAPS_CANCER.LUNG]: "Poumon",
            [ONAPS_CANCER.PROSTATE]: "Prostate",
            [ONAPS_CANCER.KIDNEY]: "Rein",
            [ONAPS_CANCER.PANCREAS]: "Pancréas",
            [ONAPS_CANCER.CEREBRAL]: "Tumeur cérébrale",
            [ONAPS_CANCER.OTHER]: "Autres"
        },
        neurologic: {
            [ONAPS_NEUROLOGIC.STROKE]: "Accident vasculaire cérébral",
            [ONAPS_NEUROLOGIC.ALZHEIMER]: "Alzheimer / démence",
            [ONAPS_NEUROLOGIC.SCLEROSIS]: "Sclérose en plaques",
            [ONAPS_NEUROLOGIC.PARKINSON]: "Parkinson",
            [ONAPS_NEUROLOGIC.MYOPATHY]: "Myopathies",
            [ONAPS_NEUROLOGIC.OTHER]: "Autres"
        },
        psychiatry: {
            [ONAPS_PSYCHIATRY.DEPRESSION]: "Dépression",
            [ONAPS_PSYCHIATRY.ANXIETY]: "Anxiété",
            [ONAPS_PSYCHIATRY.BURN_OUT]: "Burn-out",
            [ONAPS_PSYCHIATRY.SCHIZOPHRENIA]: "Schyzophrénie",
            [ONAPS_PSYCHIATRY.OTHER]: "Autres"
        },
        musculoskeletal: {
            [ONAPS_MUSCULOSKELETAL.LOW_BACK_PAIN]: "Lombalgies",
            [ONAPS_MUSCULOSKELETAL.OSTEOARTHRITIS]: "Arthrose avec limitation fonctionnelle",
            [ONAPS_MUSCULOSKELETAL.RHEUMATOID_ARTHRITIS]: "Polyarthrite rhumatoïde",
            [ONAPS_MUSCULOSKELETAL.ANKYLOSING_SPONDYLITIS]: "Spondylarthrite ankylosante",
            [ONAPS_MUSCULOSKELETAL.INFLAMMATORY_RHEUMATISM]: "Autres rhumatismes inflammatoires",
            [ONAPS_MUSCULOSKELETAL.OTHER]: "Autres"
        },
        respiratory: {
            [ONAPS_RESPIRATORY.CHRONIC_OBSTRUCTIVE_PULMONARY_DISEASE]: "Broncho pneumopathie chronique obstructive",
            [ONAPS_RESPIRATORY.ASTHMA]: "Asthme",
            [ONAPS_RESPIRATORY.RESPIRATORY_FAILURE]: "Insuffisance respiratoire",
            [ONAPS_RESPIRATORY.SLEEP_APNEA]: "Apnée du sommeil",
            [ONAPS_RESPIRATORY.OTHER]: "Autres"
        },
        infectious: {
            [ONAPS_INFECTIOUS.HIV]: "VIH",
            [ONAPS_INFECTIOUS.COVID]: "COVID",
            [ONAPS_INFECTIOUS.OTHER]: "Autres"
        },
        kidney: {
            [ONAPS_KIDNEY.KIDNEY_FAILURE_DIALYSED]: "Insuffisance rénale dialysée",
            [ONAPS_KIDNEY.KIDNEY_FAILURE_WITHOUT_DIALYSIS]: "Insuffisance rénale non dialysée",
            [ONAPS_KIDNEY.OTHER]: "Autres"
        },
        intestinal: {
            [ONAPS_INTESTINAL.CROHN]: "Maladie de Crohn",
            [ONAPS_INTESTINAL.ULCERATIVE_COLITIS]: "Rectocolite hémorragique",
            [ONAPS_INTESTINAL.OTHER]: "Autres"
        },
        functional: {
            [ONAPS_FUNCTIONAL.FIBROMYALGIA]: "Fibromyalgie",
            [ONAPS_FUNCTIONAL.CHRONIC_PAIN]: "Douleurs chroniques",
            [ONAPS_FUNCTIONAL.OTHER]: "Autres"
        },
        transplanted: {
            [ONAPS_TRANSPLANTED.KIDNEY]: "Rein",
            [ONAPS_TRANSPLANTED.HEART]: "Coeur",
            [ONAPS_TRANSPLANTED.LIVER]: "Foie",
            [ONAPS_TRANSPLANTED.OTHER]: "Autres"
        },
        pregnancy: {
            [ONAPS_PREGNANCY.PRE_PREGNANCY]: "Pré grossesse",
            [ONAPS_PREGNANCY.POST_PREGNANCY]: "Post grossesse"
        },
        age: {
            [ONAPS_AGE.FALL_PREVENTION]: "Prévention des chutes",
            [ONAPS_AGE.SARCOPENIA]: "Sarcopénie"
        }
    },
    sportActivityMode: {
        [SPORT_ACTIVITY_MODE.APA_PROGRAM]: "Programme d'APAS",
        [SPORT_ACTIVITY_MODE.ASSOCIATION]: "Pratique encadrée en association",
        [SPORT_ACTIVITY_MODE.FREE_PRACTICE]: "Pratique libre",
        [SPORT_ACTIVITY_MODE.PRACTICE_WITH_PROFESSIONAL]: "Pratique encadrée par un professionnel du soin"
    }
};

// export type _I18nOnaps = typeof _fr;

const _en: _I18nOnaps = {
    pcs: {
        [PCS.FARMER]: "Farmer",
        [PCS.EMPLOYEE]: "Employee",
        [PCS.INTERMEDIARY]: "Intermediary profession",
        [PCS.LEADER]: "Director",
        [PCS.MANAGER]: "Manager",
        [PCS.OTHER_INACTIVE]: "Other inactive",
        [PCS.RETIRED]: "Retired",
        [PCS.STUDENT]: "Student",
        [PCS.WORKER]: "Worker"
    },
    // TODO Add english translation
    stateOrPathologies: {
        general: {
            [ONAPS_STATE_OR_PATHOLOGIES.OVERWEIGHT]: "Surpoids / Obésité",
            [ONAPS_STATE_OR_PATHOLOGIES.HEART]: "Cardiovasculaires",
            [ONAPS_STATE_OR_PATHOLOGIES.DIABETES]: "Diabètes",
            [ONAPS_STATE_OR_PATHOLOGIES.CANCER]: "Cancers",
            [ONAPS_STATE_OR_PATHOLOGIES.NEUROLOGIC]: "Neurologies",
            [ONAPS_STATE_OR_PATHOLOGIES.PSYCHIATRY]: "Psychiatrie",
            [ONAPS_STATE_OR_PATHOLOGIES.MUSCULOSKELETAL]: "Ostéoarticulaires",
            [ONAPS_STATE_OR_PATHOLOGIES.RESPIRATORY]: "Respiratoires",
            [ONAPS_STATE_OR_PATHOLOGIES.INFECTIOUS]: "Infectieuses",
            [ONAPS_STATE_OR_PATHOLOGIES.KIDNEY]: "Rénales",
            [ONAPS_STATE_OR_PATHOLOGIES.INTESTINAL]: "Intestinales chroniques inflammatoires",
            [ONAPS_STATE_OR_PATHOLOGIES.FUNCTIONAL]: "Symptômes fonctionnels",
            [ONAPS_STATE_OR_PATHOLOGIES.TRANSPLANTED]: "Transplantés",
            [ONAPS_STATE_OR_PATHOLOGIES.OTHER]: "Autres",
            [ONAPS_STATE_OR_PATHOLOGIES.AGE]: "Vieillissement",
            [ONAPS_STATE_OR_PATHOLOGIES.PREGNANCY]: "Grossesse non pathologique"
        },
        overweight: {
            [ONAPS_OVERWEIGHT.OBESITY]: "Obésité",
            [ONAPS_OVERWEIGHT.OVERWEIGHT]: "Surpoids",
            [ONAPS_OVERWEIGHT.PRE_BARIATRIC]: "Préparation chirurgie bariatrique",
            [ONAPS_OVERWEIGHT.POST_BARIATRIC]: "Post chirurgie bariatrique",
            [ONAPS_OVERWEIGHT.METABOLIC]: "Syndrome métabolique",
            [ONAPS_OVERWEIGHT.STEATOSIS]: "Stéatose hépatique non alcoolique",
            [ONAPS_OVERWEIGHT.OTHER]: "Autres"
        },
        heart: {
            [ONAPS_HEART.HYPERTENSION]: "Hypertension artérielle",
            [ONAPS_HEART.CORONARY]: "Infarctus du myocarde / maladie coronaire",
            [ONAPS_HEART.HEART_FAILURE]: "Insuffisance cardiaque",
            [ONAPS_HEART.RHYTHM_TROUBLE]: "Trouble du rythme cardiaque",
            [ONAPS_HEART.CONGENITAL]: "Cardiopathie congénitale",
            [ONAPS_HEART.VALVULAR]: "Maladies valvulaires",
            [ONAPS_HEART.PULMONARY_EMBOLISM]: "Embolie pulmonaire",
            [ONAPS_HEART.OTHER]: "Autres"
        },
        diabetes: {
            [ONAPS_DIABETES.TYPE_2_UNTREATED]: "Type 2 non traité",
            [ONAPS_DIABETES.TYPE_2_TREATED]: "Type 2 traité sans complications",
            [ONAPS_DIABETES.TYPE_2_COMPLICATIONS]: "Type 2 traité avec complication",
            [ONAPS_DIABETES.TYPE_1]: "Type 1 non compliqué",
            [ONAPS_DIABETES.TYPE_1_COMPLICATIONS]: "Type 1 compliqué",
            [ONAPS_DIABETES.GESTATIONAL]: "Gestationnel"
        },
        cancer: {
            [ONAPS_CANCER.BREAST]: "Sein",
            [ONAPS_CANCER.DIGESTIVE]: "Colon / digestifs",
            [ONAPS_CANCER.UTERUS]: "Utérus",
            [ONAPS_CANCER.LUNG]: "Poumon",
            [ONAPS_CANCER.PROSTATE]: "Prostate",
            [ONAPS_CANCER.KIDNEY]: "Rein",
            [ONAPS_CANCER.PANCREAS]: "Pancréas",
            [ONAPS_CANCER.CEREBRAL]: "Tumeur cérébrale",
            [ONAPS_CANCER.OTHER]: "Autres"
        },
        neurologic: {
            [ONAPS_NEUROLOGIC.STROKE]: "Accident vasculaire cérébral",
            [ONAPS_NEUROLOGIC.ALZHEIMER]: "Alzheimer / démence",
            [ONAPS_NEUROLOGIC.SCLEROSIS]: "Sclérose en plaques",
            [ONAPS_NEUROLOGIC.PARKINSON]: "Parkinson",
            [ONAPS_NEUROLOGIC.MYOPATHY]: "Myopathies",
            [ONAPS_NEUROLOGIC.OTHER]: "Autres"
        },
        psychiatry: {
            [ONAPS_PSYCHIATRY.DEPRESSION]: "Dépression",
            [ONAPS_PSYCHIATRY.ANXIETY]: "Anxiété",
            [ONAPS_PSYCHIATRY.BURN_OUT]: "Burn-out",
            [ONAPS_PSYCHIATRY.SCHIZOPHRENIA]: "Schyzophrénie",
            [ONAPS_PSYCHIATRY.OTHER]: "Autres"
        },
        musculoskeletal: {
            [ONAPS_MUSCULOSKELETAL.LOW_BACK_PAIN]: "Lombalgies",
            [ONAPS_MUSCULOSKELETAL.OSTEOARTHRITIS]: "Arthrose avec limitation fonctionnelle",
            [ONAPS_MUSCULOSKELETAL.RHEUMATOID_ARTHRITIS]: "Polyarthrite rhumatoïde",
            [ONAPS_MUSCULOSKELETAL.ANKYLOSING_SPONDYLITIS]: "Spondylarthrite ankylosante",
            [ONAPS_MUSCULOSKELETAL.INFLAMMATORY_RHEUMATISM]: "Autres rhumatismes inflammatoires",
            [ONAPS_MUSCULOSKELETAL.OTHER]: "Autres"
        },
        respiratory: {
            [ONAPS_RESPIRATORY.CHRONIC_OBSTRUCTIVE_PULMONARY_DISEASE]: "Broncho pneumopathie chronique obstructive",
            [ONAPS_RESPIRATORY.ASTHMA]: "Asthme",
            [ONAPS_RESPIRATORY.RESPIRATORY_FAILURE]: "Insuffisance respiratoire",
            [ONAPS_RESPIRATORY.SLEEP_APNEA]: "Apnée du sommeil",
            [ONAPS_RESPIRATORY.OTHER]: "Autres"
        },
        infectious: {
            [ONAPS_INFECTIOUS.HIV]: "VIH",
            [ONAPS_INFECTIOUS.COVID]: "COVID",
            [ONAPS_INFECTIOUS.OTHER]: "Autres"
        },
        kidney: {
            [ONAPS_KIDNEY.KIDNEY_FAILURE_DIALYSED]: "Insuffisance rénale dialysée",
            [ONAPS_KIDNEY.KIDNEY_FAILURE_WITHOUT_DIALYSIS]: "Insuffisance rénale non dialysée",
            [ONAPS_KIDNEY.OTHER]: "Autres"
        },
        intestinal: {
            [ONAPS_INTESTINAL.CROHN]: "Maladie de Crohn",
            [ONAPS_INTESTINAL.ULCERATIVE_COLITIS]: "Rectocolite hémorragique",
            [ONAPS_INTESTINAL.OTHER]: "Autres"
        },
        functional: {
            [ONAPS_FUNCTIONAL.FIBROMYALGIA]: "Fibromyalgie",
            [ONAPS_FUNCTIONAL.CHRONIC_PAIN]: "Douleurs chroniques",
            [ONAPS_FUNCTIONAL.OTHER]: "Autres"
        },
        transplanted: {
            [ONAPS_TRANSPLANTED.KIDNEY]: "Rein",
            [ONAPS_TRANSPLANTED.HEART]: "Coeur",
            [ONAPS_TRANSPLANTED.LIVER]: "Foie",
            [ONAPS_TRANSPLANTED.OTHER]: "Autres"
        },
        pregnancy: {
            [ONAPS_PREGNANCY.PRE_PREGNANCY]: "Pré grossesse",
            [ONAPS_PREGNANCY.POST_PREGNANCY]: "Post grossesse"
        },
        age: {
            [ONAPS_AGE.FALL_PREVENTION]: "Prévention des chutes",
            [ONAPS_AGE.SARCOPENIA]: "Sarcopénie"
        }
    },
    sportActivityMode: {
        [SPORT_ACTIVITY_MODE.APA_PROGRAM]: "APA program(s)",
        [SPORT_ACTIVITY_MODE.ASSOCIATION]: "Practice within a club",
        [SPORT_ACTIVITY_MODE.FREE_PRACTICE]: "Free practice",
        [SPORT_ACTIVITY_MODE.PRACTICE_WITH_PROFESSIONAL]: "Practice with a health professional"
    }
};

/**
 * Return value in required language
 * @param lang Chosen lang
 * @returns 
 */
export function I18nOnaps(lang?: string): _I18nOnaps {
    lang = lang ?? getDefaultLang();
    switch (lang) {
        case "en":
            return _en;
        case "fr":
        default:
            return _fr;
    }
}